import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import PublicMilestoneTracker from '@/components/PublicMilestoneTracker';
import { formatCurrency, isPartialMoneyAmount } from '@/utils/helper';

// TODO: fetch these from backend settings
const MIN_AMOUNT = 4.2;
const MAX_AMOUNT = 5000;

/** @param {import("prop-types").InferProps<typeof StepAmountPropTypes>} props */
export default function StepAmount({ formData, setFormData, nextStep, eventId }) {
  const { t, i18n } = useTranslation();

  const [localError, setLocalError] = useState('');

  const handleChange = event => {
    const { name, value, type, checked } = event.target;
    if (name === 'amount') {
      if (!isPartialMoneyAmount(value)) {
        return;
      }
      // Don't keep the field in an error state while it's being corrected
      setLocalError('');
    }
    if (name === 'message' && value.length > 500) {
      return;
    }
    if (name === 'username' && value.length > 50) {
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  /** @returns {boolean} Whether the amount is within the accepted range. */
  const validateAmount = () => {
    const amount =
      typeof formData.amount === 'number' ? formData.amount : Number.parseFloat(formData.amount);
    if (Number.isNaN(amount) || amount < MIN_AMOUNT || amount > MAX_AMOUNT) {
      setLocalError(
        t('donation.amountOutOfRange', {
          min: formatCurrency(MIN_AMOUNT, i18n.language),
          max: formatCurrency(MAX_AMOUNT, i18n.language),
        }),
      );
      return false;
    }
    setLocalError('');
    return true;
  };

  const handleNext = () => {
    if (validateAmount()) {
      nextStep();
    }
  };

  return (
    <Box
      component="form"
      onSubmit={event => {
        event.preventDefault();
        handleNext();
      }}
    >
      <Typography gutterBottom variant="h5">
        {t('donation.titleAmount')}
      </Typography>

      <PublicMilestoneTracker
        eventId={eventId}
        userAmount={Number(formData.amount) || 0}
        sx={[{ marginBottom: 2.5 }]}
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            required
            fullWidth
            label={t('donation.amount')}
            name="amount"
            type="text"
            value={formData.amount}
            onChange={handleChange}
            error={!!localError}
            helperText={localError}
            onBlur={() => {
              if (formData.amount !== '') {
                validateAmount();
              }
            }}
            slotProps={{
              input: { endAdornment: <Typography variant="body1">$&nbsp;CAD</Typography> },
              htmlInput: { inputMode: 'decimal' },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={t('donation.displayName')}
            name="username"
            value={formData.username}
            onChange={handleChange}
            slotProps={{ htmlInput: { maxLength: 50 } }}
            helperText={t('donation.displayName.helper')}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            label={t('donation.message')}
            name="message"
            rows={3}
            value={formData.message}
            onChange={handleChange}
            slotProps={{ htmlInput: { maxLength: 500 } }}
            helperText={`${(formData.message || '').length}/500`}
          />
        </Grid>
        <Grid size={12}>
          <FormControlLabel
            label={t('donation.anonymous')}
            control={
              <Checkbox name="isAnonymous" checked={formData.isAnonymous} onChange={handleChange} />
            }
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 3 }}>
        <Button type="submit" variant="contained">
          {t('common.next')}
        </Button>
      </Box>
    </Box>
  );
}

const StepAmountPropTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  formData: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    username: PropTypes.string,
    message: PropTypes.string,
    isAnonymous: PropTypes.bool,
  }).isRequired,
  nextStep: PropTypes.func.isRequired,
  setFormData: PropTypes.func.isRequired,
};
StepAmount.propTypes = StepAmountPropTypes;
